import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLive } from "../../state/use-live.js";
import { storage } from "../../state/storage.js";
import { useUiStore } from "../../state/ui-store.js";
import { useStreamingBuffer } from "../../state/use-streaming-buffer.js";
import { sendUserMessage, regenerateLast, estimateContextUsage } from "../../state/chat-actions.js";
import { restartGameSession } from "../../state/game-session.js";
import {
  editMessage,
  swipeMessage,
  deleteMessage,
  deleteActiveVariant,
  forkChatAt,
} from "../../state/message-actions.js";
import { LazyMessageContent } from "./LazyMessageContent.js";
import { ChatSettingsPanel } from "./ChatSettingsPanel.js";
import { PromptPreview } from "./PromptPreview.js";
import { HudRenderer, ViewSpecRenderer } from "./GameWidgets.js";
import { gameModules } from "../../state/game-modules.js";
import { getModuleTurnDebug, type ModuleTurnDebug } from "../../state/game-turn-debug.js";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";
import { Activity, ArrowLeft, Settings, Eye, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import {
  applyDisplayRegex,
  providerSupportsCapability,
  renderRoqeetText,
  ROLEPLAY_MODULE_ID,
  stripTurnTagsForDisplay,
  type Chat,
  type ContextUsage,
  type Message,
  type Script,
  type ViewSpec,
} from "@roqeet/core";

/**
 * 채팅방 화면 — 결정 #13/#14/#16/#21을 잇는 곳.
 *  - 메시지는 useLiveQuery로 반응형 구독(결정 #13). 전송/재생성 후 자동 갱신.
 *  - 스트리밍은 ref버퍼+rAF로 배치 렌더(결정 #14). 진행 중엔 임시 말풍선 표시.
 *  - 표시 직전 Roqeet 인라인 값 전개. @캐릭터/@유저 등 치환.
 *  - 스와이프(재생성)/취소 버튼(결정 #16).
 */
export function ChatRoomView({ chatId }: { chatId: string }) {
  const { t } = useT();
  const setActiveChatId = useUiStore((s) => s.setActiveChatId);
  const setView = useUiStore((s) => s.setView);
  const messages = useLive<Message[]>(() => storage.liveMessages(chatId), [chatId]);

  const [chat, setChat] = useState<Chat | undefined>();
  const [names, setNames] = useState<{ char: string; user: string }>({ char: "", user: "" });
  const [scripts, setScripts] = useState<Script[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [usage, setUsage] = useState<ContextUsage | undefined>(undefined);
  // 렌더 윈도잉(결정 #9/#11): 긴 대화는 최근 N개만 그려 6s+에서 마크다운 블록
  // 수백 개를 동시에 파싱하지 않게 한다. "더보기"로 위쪽을 점진 확장.
  const RENDER_WINDOW = 40;
  const [visibleCount, setVisibleCount] = useState(RENDER_WINDOW);
  const [hasProvider, setHasProvider] = useState(true);
  const stream = useStreamingBuffer();
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // 채팅 + 표시 이름(Roqeet 인라인 값용) 로드.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const c = await storage.chats.get(chatId);
      if (!alive) return;
      setChat(c);
      if (!c) return;
      const charSeat = c.participants.find((p) => p.type === "character");
      const userSeat = c.participants.find((p) => p.type === "user");
      const [char, persona] = await Promise.all([
        charSeat?.characterId ? storage.characters.get(charSeat.characterId) : undefined,
        userSeat?.personaId ? storage.personas.get(userSeat.personaId) : undefined,
      ]);
      const personas = persona ? [persona] : await storage.personas.list();
      const settings = await storage.getSettings();
      const defPersona = persona ?? personas.find((p) => p.id === settings?.defaultPersonaId) ?? personas[0];
      // 캐릭터에 붙은 정규식 디스플레이 스크립트 로드(결정 #21 정규식 단계).
      const charScripts = char?.scriptIds.length
        ? (await storage.scripts.getMany(char.scriptIds)).filter(
            (s): s is Script => !!s && s.language === "regex",
          )
        : [];
      if (alive) {
        setNames({ char: char?.name ?? "AI", user: defPersona?.name ?? "You" });
        setScripts(charScripts);
      }
      // 온보딩: 활성 프로바이더가 없으면 배너로 안내(전송 전 사전 고지).
      const providers = await storage.providerProfiles.list();
      if (alive) setHasProvider(providers.some((p) => p.enabled && providerSupportsCapability(p, "chat")));
    })();
    return () => {
      alive = false;
    };
  }, [chatId, reloadKey]);

  // 메시지/스트림 변동 시 맨 아래로 스크롤.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, stream.text]);

  // 게임 모듈 세션(결정 #25/#28): 턴이 끝나면 moduleState가 갱신되므로, 스트리밍이
  // 멈춘 뒤 chat을 가볍게 다시 읽어 HUD/위젯이 최신 상태를 반영하게 한다. 자유대화
  // (롤플레잉)는 moduleState가 없어 사실상 비용이 없다.
  useEffect(() => {
    if (stream.streaming) return;
    let alive = true;
    void storage.chats.get(chatId).then((c) => {
      if (alive && c) setChat(c);
    });
    return () => {
      alive = false;
    };
  }, [chatId, messages, stream.streaming]);

  // 컨텍스트 사용량 추정(결정 #12). 스트리밍 중엔 부하를 피해 생략하고, 메시지가
  // 확정된 뒤(messages 변동)에만 다시 계산한다.
  useEffect(() => {
    if (stream.streaming) return;
    let alive = true;
    void estimateContextUsage(chatId).then((u) => {
      if (alive) setUsage(u);
    });
    return () => {
      alive = false;
    };
  }, [chatId, messages, reloadKey, stream.streaming]);

  const roqeet = useMemo(() => ({ char: names.char, user: names.user }), [names]);
  // 결정 #21 렌더 파이프라인: Roqeet 인라인 전개 → 정규식 디스플레이 스크립트 → 마크다운+살균.
  const render = (text: string) => applyDisplayRegex(renderRoqeetText(text, roqeet), scripts);

  // 게임 모듈 + 뷰 스펙(결정 #25/#27). 구조화 게임이면 현재 moduleState로 위젯을
  // 선언하고, 자유대화(롤플레잉)는 viewSpec 훅이 없어 위젯이 없다(메시지 목록만).
  const gameModule = chat ? gameModules.get(chat.moduleId ?? ROLEPLAY_MODULE_ID) : undefined;
  const viewSpec: ViewSpec | undefined = useMemo(() => {
    if (!gameModule?.viewSpec || !chat) return undefined;
    try {
      return gameModule.viewSpec(chat.moduleState ?? {});
    } catch {
      return undefined;
    }
  }, [gameModule, chat]);

  // 게임 종료 감지(결정 #28): processGameTurn이 종료 시 moduleState.ended를 정규화해
  // 저장하므로, 그 값으로 입력을 막고 재시작 배너를 띄운다. 자유대화는 ended가 없어 false.
  const moduleState = chat?.moduleState as { ended?: boolean; endingLabel?: string } | undefined;
  const gameEnded = !!(gameModule?.applyTurn && moduleState?.ended);
  const endingLabel = moduleState?.endingLabel;

  const handleRestart = async () => {
    if (busy) return;
    try {
      await restartGameSession(chatId, (k, ...args) => t(k as any, ...args));
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // 채팅 전환 시 렌더 윈도우 초기화(결정 #9/#11).
  useEffect(() => {
    setVisibleCount(RENDER_WINDOW);
  }, [chatId]);

  // 렌더 윈도잉: 최근 visibleCount개만 그린다(결정 #9/#11). 새 메시지가 와도
  // 항상 꼬리가 보이도록 슬라이스는 뒤에서 자른다.
  const allMessages = useMemo(() => messages ?? [], [messages]);
  const hiddenCount = Math.max(0, allMessages.length - visibleCount);
  const visibleMessages = hiddenCount > 0 ? allMessages.slice(hiddenCount) : allMessages;
  const turnTimelineRows = useMemo(
    () =>
      allMessages
        .map((message) => ({ message, debug: getModuleTurnDebug(message) }))
        .filter((row): row is { message: Message; debug: ModuleTurnDebug } => !!row.debug),
    [allMessages],
  );

  const handleSend = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    await submitText(text);
  };

  // 선택지/투표 위젯이 사용자 입력을 구동한다(결정 #27). 고른 선택지 텍스트를 그대로
  // 사용자 메시지로 보낸다 — 게임 모듈이 그걸 받아 다음 턴을 진행한다.
  const submitText = async (text: string) => {
    if (!text || busy) return;
    setError(null);
    setBusy(true);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await sendUserMessage(
        chatId,
        text,
        {
          onStart: () => stream.begin(),
          onDelta: (d) => stream.append(d),
          onReasoning: (d) => stream.appendReasoning(d),
        },
        ac.signal,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      stream.end();
      setBusy(false);
      abortRef.current = null;
    }
  };

  const handleChoice = (choiceText: string) => {
    if (busy) return;
    void submitText(choiceText);
  };

  // 투표 위젯(결정 #27, 소셜 추리). 고른 후보 id를 현재 viewSpec에서 라벨로 변환해
  // 의미 있는 사용자 메시지로 전송한다(모듈이 그 텍스트로 다음 턴을 진행).
  const handleVote = (candidateId: string) => {
    if (busy) return;
    const voteWidget = [...(viewSpec?.widgets ?? []), ...(viewSpec?.hud ?? [])].find(
      (w): w is Extract<typeof w, { kind: "vote" }> => w.kind === "vote",
    );
    const candidate = voteWidget?.candidates.find((c) => c.id === candidateId);
    const label = candidate?.label ?? candidateId;
    void submitText(t("chat.voteMessage", { label }));
  };

  const handleRegenerate = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await regenerateLast(
        chatId,
        {
          onStart: () => stream.begin(),
          onDelta: (d) => stream.append(d),
          onReasoning: (d) => stream.appendReasoning(d),
        },
        ac.signal,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      stream.end();
      setBusy(false);
      abortRef.current = null;
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  return (
    <div className="flex h-full flex-col">
      {/* 헤더: 뒤로가기 + 제목 + 설정 */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        <button
          aria-label={t("chat.backToList")}
          className="rounded p-2 text-ink-soft hover:bg-bg-raised"
          onClick={() => setActiveChatId(null)}
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <button
          className="min-w-0 flex-1 truncate text-left text-sm font-medium text-ink hover:text-accent-soft"
          onClick={() => setSettingsOpen(true)}
          title={t("chat.settings")}
        >
          {chat?.title ?? t("chat.titleFallback")}
        </button>
        {usage && usage.budget > 0 && (
          <span
            className={[
              "shrink-0 rounded px-1.5 py-0.5 text-xs tabular-nums",
              usage.over ? "bg-red-950/50 text-red-300" : "text-ink-faint",
            ].join(" ")}
            title={t("chat.usageTitle", {
              used: usage.used,
              budget: usage.budget,
              over: [
                usage.over ? t("chat.usageOver") : "",
                usage.totalCostUsd != null
                  ? `~${formatUsd(usage.totalCostUsd)} (${usage.pricingSource ?? "pricing"})`
                  : "",
              ].filter(Boolean).join(" "),
            })}
          >
            {Math.round(usage.ratio * 100)}%
            {usage.totalCostUsd != null && (
              <span className="ml-1 text-[10px]">{formatUsd(usage.totalCostUsd)}</span>
            )}
          </span>
        )}
        {gameModule?.applyTurn && (
          <button
            aria-label="턴 타임라인"
            className={[
              "rounded p-2 text-ink-soft hover:bg-bg-raised disabled:opacity-40",
              turnTimelineRows.length ? "text-accent-soft" : "",
            ].join(" ")}
            onClick={() => setTimelineOpen(true)}
            title="턴 디버거 / 상태 변화 타임라인"
            disabled={turnTimelineRows.length === 0}
          >
            <Activity size={20} aria-hidden="true" />
          </button>
        )}
        <button
          aria-label={t("chat.previewPromptTitle")}
          className="rounded p-2 text-ink-soft hover:bg-bg-raised"
          onClick={() => setPreviewOpen(true)}
          title={t("chat.previewPromptTitle")}
        >
          <Eye size={20} aria-hidden="true" />
        </button>
        <button
          aria-label={t("chat.settings")}
          className="rounded p-2 text-ink-soft hover:bg-bg-raised"
          onClick={() => setSettingsOpen(true)}
        >
          <Settings size={20} aria-hidden="true" />
        </button>
      </div>

      {settingsOpen && chat && (
        <ChatSettingsPanel
          chat={chat}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}

      {previewOpen && <PromptPreview chatId={chatId} onClose={() => setPreviewOpen(false)} />}

      {timelineOpen && (
        <TurnTimelinePanel rows={turnTimelineRows} onClose={() => setTimelineOpen(false)} />
      )}

      {/* 게임 HUD(결정 #27): 스탯 바·위치 등 고정 표시. 자유대화는 빈 hud라 안 그려짐. */}
      {viewSpec && <HudRenderer spec={viewSpec} />}

      {/* 메시지 목록 */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
        <ul className="mx-auto flex max-w-2xl flex-col gap-3">
          {/* 렌더 윈도잉: 가려진 과거 메시지가 있으면 더보기로 확장(결정 #9/#11). */}
          {hiddenCount > 0 && (
            <li className="flex justify-center">
              <button
                className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised"
                onClick={() => setVisibleCount((n) => n + RENDER_WINDOW)}
              >
                {t("chat.loadMore", { count: Math.min(RENDER_WINDOW, hiddenCount), hidden: hiddenCount })}
              </button>
            </li>
          )}
          {visibleMessages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              render={render}
              busy={busy}
              onFork={async () => {
                const id = await forkChatAt(chatId, m.order);
                if (id) setActiveChatId(id);
              }}
            />
          ))}

          {/* 스트리밍 임시 말풍선(결정 #14): 완료 시 실제 메시지로 대체됨. 구조화 게임은
              아직 닫히지 않은 태그가 깜빡이지 않도록 표시 전에 태그를 숨긴다(결정 #26). */}
          {stream.streaming && (
            <li className="flex flex-col items-start">
              {stream.reasoning && <ReasoningBlock text={stream.reasoning} streaming />}
              <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-tl-sm bg-bg-raised px-3 py-2 text-sm text-ink">
                {render(
                  gameModule?.viewSpec ? stripTurnTagsForDisplay(stream.text) : stream.text,
                ) || <span className="text-ink-faint">…</span>}
              </div>
            </li>
          )}
        </ul>

        {messages?.length === 0 && !stream.streaming && (
          <p className="px-1 py-8 text-center text-sm text-ink-faint">
            {t("chat.emptyStart")}
          </p>
        )}
      </div>

      {/* 에러 배너 */}
      {error && (
        <div className="border-t border-line bg-red-950/40 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}

      {/* 온보딩: 활성 프로바이더가 없으면 전송 전에 안내(결정 #8 익명도 100% 사용). */}
      {!hasProvider && (
        <div className="flex items-center justify-between gap-2 border-t border-line bg-accent/15 px-3 py-2 text-xs text-ink-soft">
          <span>{t("chat.providerNeeded")}</span>
          <button
            className="shrink-0 rounded-lg bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent-soft"
            onClick={() => {
              setActiveChatId(null);
              setView("settings");
            }}
          >
            {t("chat.goToSettings")}
          </button>
        </div>
      )}

      {/* 게임 위젯(결정 #27): 선택지·인벤토리·투표·알림 등을 입력창 위에 띄운다. 선택지나
          투표를 고르면 그 텍스트가 사용자 입력으로 전송돼 다음 턴을 진행한다. 자유대화는 위젯 없음. */}
      {viewSpec && (
        <div className="shrink-0 border-t border-line pt-2">
          <ViewSpecRenderer spec={viewSpec} onChoice={handleChoice} onVote={handleVote} busy={busy} />
        </div>
      )}

      {/* 게임 종료 배너(결정 #28): 구조화 게임이 끝나면 입력 대신 결과 + 다시 시작을 띄운다. */}
      {gameEnded ? (
        <div className="shrink-0 border-t border-line p-3">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-2 rounded-xl border border-line bg-bg-soft px-4 py-4 text-center">
            <span className="text-sm font-medium text-ink">
              {t("chat.gameOver")}{endingLabel ? ` — ${endingLabel}` : ""}
            </span>
            <span className="text-xs text-ink-faint">
              {t("chat.gameOverHint")}
            </span>
            <button
              className="mt-1 rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
              onClick={handleRestart}
              disabled={busy}
            >
              {t("chat.restart")}
            </button>
          </div>
        </div>
      ) : (
        /* 입력 영역 */
        <div className="shrink-0 border-t border-line p-2">
        <div className="mx-auto flex max-w-2xl items-end gap-2">
          {messages && messages.length > 0 && !busy && (
            <button
              aria-label={t("chat.regenLast")}
              className="rounded-lg border border-line px-2 py-2 text-ink-soft hover:bg-bg-raised"
              onClick={handleRegenerate}
              title={t("chat.regenTitle")}
            >
              <RefreshCw size={18} aria-hidden="true" />
            </button>
          )}
          <textarea
            className="min-h-[40px] max-h-40 flex-1 resize-none rounded-xl border border-line bg-bg-soft px-3 py-2 text-ink outline-none focus:border-accent"
            placeholder={t("chat.inputPlaceholder")}
            rows={1}
            value={input}
            disabled={busy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
          {busy ? (
            <button
              className="rounded-xl bg-bg-raised px-4 py-2 text-sm font-medium text-ink-soft"
              onClick={handleStop}
            >
              {t("chat.stop")}
            </button>
          ) : (
            <button
              className="rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
              onClick={handleSend}
              disabled={!input.trim()}
            >
              {t("chat.send")}
            </button>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function formatUsd(value: number): string {
  if (value === 0) return "$0";
  if (value < 0.0001) return `$${value.toFixed(6)}`;
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/**
 * 한 메시지 말풍선 — 결정 #16 상호작용 포함.
 *  - 사용자=오른쪽, 캐릭터/내레이터=왼쪽, 시스템=가운데.
 *  - variant가 여럿이면 ‹ N/M › 스와이프 컨트롤.
 *  - 호버/포커스 시 편집·삭제·분기 액션 노출.
 *  - 편집 모드에서는 textarea로 활성 variant 텍스트를 제자리 수정(편집 이력 보존).
 */
function MessageBubble({
  message,
  render,
  busy,
  onFork,
}: {
  message: Message;
  render: (text: string) => string;
  busy: boolean;
  onFork: () => void | Promise<void>;
}) {
  const { t } = useT();
  const variant = message.variants[message.activeVariant];
  const rawContent = variant?.content ?? "";
  const content = render(rawContent);
  const isUser = message.senderType === "user";
  const isSystem = message.senderType === "system";
  const turnDebug = !isUser ? getModuleTurnDebug(message) : undefined;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rawContent);
  const hasVariants = message.variants.length > 1;

  if (isSystem) {
    return (
      <li className="flex justify-center">
        <div className="rounded-full bg-bg-soft px-3 py-1 text-xs text-ink-faint">{content}</div>
      </li>
    );
  }

  const beginEdit = () => {
    setDraft(rawContent);
    setEditing(true);
  };
  const saveEdit = async () => {
    await editMessage(message.id, draft);
    setEditing(false);
  };

  return (
    <li className={["group flex flex-col", isUser ? "items-end" : "items-start"].join(" ")}>
      {message.senderName && (
        <span className="mb-0.5 px-1 text-xs text-ink-faint">{message.senderName}</span>
      )}
      {/* 추론(thinking) 표시: 캐릭터 응답에 reasoning이 있으면 접은 채로(결정 #14). */}
      {!isUser && !editing && variant?.reasoning && <ReasoningBlock text={variant.reasoning} />}
      <div
        className={[
          "max-w-[85%] break-words rounded-2xl px-3 py-2 text-sm",
          isUser
            ? "rounded-tr-sm bg-accent text-white"
            : "rounded-tl-sm bg-bg-raised text-ink",
        ].join(" ")}
      >
        {editing ? (
          <div className="flex flex-col gap-2">
            <textarea
              className="min-h-[60px] w-full min-w-[200px] resize-y rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-ink outline-none focus:border-accent"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                className="rounded-md px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                onClick={() => setEditing(false)}
              >
                {t("common.cancel")}
              </button>
              <button
                className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-white"
                onClick={saveEdit}
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        ) : (
          // 커밋된 메시지는 마크다운 렌더(결정 #21). Roqeet 인라인 값은 render로 미리 전개.
          <LazyMessageContent text={content} />
        )}
      </div>

      {/* 액션 바: 스와이프 + 편집/삭제/분기 (결정 #16) */}
      {!editing && turnDebug && <GameTurnDebugBlock debug={turnDebug} />}

      {!editing && (
        <div
          className={[
            "action-reveal mt-0.5 flex flex-wrap items-center gap-1 px-1 text-xs text-ink-faint",
          ].join(" ")}
        >
          {hasVariants && (
            <>
              <button
                className="min-h-7 rounded px-1 touch-manipulation hover:bg-bg-raised disabled:opacity-30"
                aria-label={t("chat.prevResponse")}
                disabled={busy || message.activeVariant === 0}
                onClick={() => swipeMessage(message.id, -1)}
              >
                ‹
              </button>
              <span>
                {message.activeVariant + 1}/{message.variants.length}
              </span>
              <button
                className="min-h-7 rounded px-1 touch-manipulation hover:bg-bg-raised disabled:opacity-30"
                aria-label={t("chat.nextResponse")}
                disabled={busy || message.activeVariant === message.variants.length - 1}
                onClick={() => swipeMessage(message.id, 1)}
              >
                ›
              </button>
              <span className="px-0.5">·</span>
            </>
          )}
          <button className="min-h-7 rounded px-1 touch-manipulation hover:bg-bg-raised" onClick={beginEdit}>
            {t("common.edit")}
          </button>
          {hasVariants && (
            <button
              className="min-h-7 rounded px-1 touch-manipulation hover:bg-bg-raised"
              title={t("chat.deleteVariantTitle")}
              onClick={() => deleteActiveVariant(message.id)}
            >
              {t("chat.deleteVariantBtn")}
            </button>
          )}
          <button
            className="min-h-7 rounded px-1 touch-manipulation hover:bg-bg-raised"
            title={t("chat.forkTitle")}
            onClick={() => onFork()}
          >
            {t("chat.fork")}
          </button>
          <button
            className="min-h-7 rounded px-1 text-red-400 touch-manipulation hover:bg-bg-raised"
            title={t("chat.deleteMessageTitle")}
            onClick={() => deleteMessage(message.id)}
          >
            {t("common.delete")}
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * 추론(thinking) 블록 — 결정 #14. 추론 모델(o1/Claude thinking 등)이 본문과
 * 별도로 보낸 사고 과정을 접을 수 있는 형태로 표시한다. 스트리밍 중에는 펼친
 * 채로(진행 상황 노출), 커밋 후에는 접은 채로 둔다.
 */
function GameTurnDebugBlock({ debug }: { debug: ModuleTurnDebug }) {
  const [open, setOpen] = useState(false);
  const actionCount = debug.parsed.stateDelta.length + debug.parsed.choices.length + debug.parsed.rolls.length + (debug.parsed.scene ? 1 : 0);
  return (
    <div className="mt-1 max-w-[85%] overflow-hidden rounded-xl border border-line bg-bg-soft/70 text-xs">
      <button
        className="flex w-full items-center gap-1 px-2 py-1 text-left text-ink-faint hover:bg-bg-raised"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={16} className="shrink-0" /> : <ChevronRight size={16} className="shrink-0" />}
        <span className="font-medium text-ink-soft">턴 디버그</span>
        <span>상태 {debug.diff.length} · 액션 {actionCount}</span>
        {debug.diagnostics.length > 0 && <span className="text-amber-300">경고 {debug.diagnostics.length}</span>}
      </button>
      {open && <TurnDebugDetails debug={debug} />}
    </div>
  );
}

function TurnDebugDetails({ debug }: { debug: ModuleTurnDebug }) {
  return (
    <div className="grid gap-2 border-t border-line p-2 text-ink-soft">
      <DebugSection title="상태 변화">
        {debug.diff.length === 0 ? (
          <p className="text-ink-faint">이번 턴에서 저장 상태는 바뀌지 않았습니다.</p>
        ) : (
          <ul className="grid gap-1">
            {debug.diff.map((entry) => (
              <li key={entry.key} className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2 rounded border border-line bg-bg-raised px-2 py-1">
                <span className="font-medium text-accent-soft">{entry.key}</span>
                <span className="min-w-0 break-words">
                  <span className="text-ink-faint">{stateChangeLabel(entry.change)}</span>{" "}
                  {formatDebugValue(entry.before)} <span className="text-ink-faint">→</span>{" "}
                  {formatDebugValue(entry.after)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DebugSection>

      {(debug.parsed.stateDelta.length > 0 || debug.parsed.choices.length > 0 || debug.parsed.scene || debug.parsed.rolls.length > 0) && (
        <DebugSection title="AI가 제안한 구조">
          <div className="grid gap-1">
            {debug.parsed.stateDelta.map((op, index) => (
              <code key={`${op.op}-${op.key}-${index}`} className="rounded border border-line bg-bg-raised px-2 py-1 text-[11px] text-ink">
                {formatStateOp(op)}
              </code>
            ))}
            {debug.parsed.choices.length > 0 && (
              <p>
                <span className="text-ink-faint">선택지</span> {debug.parsed.choices.join(" / ")}
              </p>
            )}
            {debug.parsed.scene && (
              <p>
                <span className="text-ink-faint">장면</span> {Object.entries(debug.parsed.scene).map(([k, v]) => `${k}=${v}`).join(", ")}
              </p>
            )}
            {debug.parsed.rolls.length > 0 && (
              <p>
                <span className="text-ink-faint">주사위</span> {debug.parsed.rolls.map((roll) => roll.notation).join(", ")}
              </p>
            )}
          </div>
        </DebugSection>
      )}

      {debug.diagnostics.length > 0 && (
        <DebugSection title="경고">
          <ul className="grid gap-1">
            {debug.diagnostics.map((item, index) => (
              <li key={`${item.code}-${index}`} className="rounded border border-amber-400/30 bg-amber-500/10 px-2 py-1 text-amber-200">
                {item.message}
                {item.source && <span className="block break-words text-amber-200/70">{item.source}</span>}
              </li>
            ))}
          </ul>
        </DebugSection>
      )}

      <details className="rounded border border-line bg-bg-raised">
        <summary className="cursor-pointer px-2 py-1 text-ink-faint">상태 스냅샷</summary>
        <div className="grid gap-2 border-t border-line p-2 md:grid-cols-2">
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-soft p-2 text-[11px] text-ink">
            {JSON.stringify(debug.before, null, 2)}
          </pre>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-soft p-2 text-[11px] text-ink">
            {JSON.stringify(debug.after, null, 2)}
          </pre>
        </div>
      </details>
    </div>
  );
}

function DebugSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{title}</h4>
      {children}
    </section>
  );
}

function TurnTimelinePanel({
  rows,
  onClose,
}: {
  rows: { message: Message; debug: ModuleTurnDebug }[];
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full max-w-3xl flex-col rounded-xl border border-line bg-bg-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink">턴 디버거 / 상태 변화 타임라인</h3>
            <p className="mt-0.5 text-xs text-ink-faint">AI 응답이 어떤 구조 액션을 냈고, 런타임 상태가 어떻게 바뀌었는지 턴별로 확인합니다.</p>
          </div>
          <button className="rounded px-2 py-1 text-sm text-ink-faint hover:bg-bg-raised hover:text-ink" onClick={onClose}>
            닫기
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
          {rows.length === 0 ? (
            <p className="p-4 text-sm text-ink-faint">아직 기록된 게임 턴이 없습니다.</p>
          ) : (
            <ol className="grid gap-3">
              {rows.map(({ message, debug }, index) => (
                <li key={message.id} className="grid gap-2 rounded-lg border border-line bg-bg-raised p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="text-sm font-semibold text-ink">턴 {index + 1}</span>
                      <span className="ml-2 text-xs text-ink-faint">order {message.order}</span>
                      {message.senderName && <span className="ml-2 text-xs text-ink-faint">{message.senderName}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 text-[11px]">
                      <span className="rounded bg-bg-soft px-1.5 py-0.5 text-ink-faint">상태 {debug.diff.length}</span>
                      <span className="rounded bg-bg-soft px-1.5 py-0.5 text-ink-faint">선택지 {debug.parsed.choices.length}</span>
                      {debug.ended && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-red-300">{debug.endingLabel ?? "종료"}</span>}
                    </div>
                  </div>
                  <TurnDebugDetails debug={debug} />
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function formatStateOp(op: ModuleTurnDebug["parsed"]["stateDelta"][number]): string {
  const entity = op.entity ? ` 대상=${op.entity}` : "";
  const value = op.value == null ? "" : ` ${op.value}`;
  return `${op.op} ${op.key}${value}${entity}`;
}

function stateChangeLabel(change: ModuleTurnDebug["diff"][number]["change"]): string {
  if (change === "added") return "추가";
  if (change === "removed") return "삭제";
  return "변경";
}

function formatDebugValue(value: unknown): string {
  if (value === undefined) return "없음";
  if (typeof value === "string") return JSON.stringify(value);
  const text = JSON.stringify(value);
  if (text == null) return String(value);
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
}

function ReasoningBlock({ text, streaming }: { text: string; streaming?: boolean }) {
  const { t } = useT();
  const [open, setOpen] = useState(!!streaming);
  return (
    <div className="mb-1 max-w-[85%] overflow-hidden rounded-xl border border-line bg-bg-soft/60 text-xs">
      <button
        className="flex w-full items-center gap-1 px-2 py-1 text-left text-ink-faint hover:bg-bg-raised"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={16} className="shrink-0" /> : <ChevronRight size={16} className="shrink-0" />}
        <span>{t("chat.reasoning")}{streaming ? t("chat.reasoningStreaming") : ""}</span>
      </button>
      {open && (
        <div className="whitespace-pre-wrap break-words border-t border-line px-2 py-1.5 text-ink-soft">
          {text}
        </div>
      )}
    </div>
  );
}
